import { useCallback, useMemo } from 'react'

import { useWorkspaceLazyQueries } from 'hooks-queries/workspace'

import type { TUseWorkspaceCompanyPlanReturn, TWorkspaceLanguages, IUseWorkspaceCompanyPlanParams } from './types'

export const useWorkspaceCompanyPlan = (props: IUseWorkspaceCompanyPlanParams): TUseWorkspaceCompanyPlanReturn => {
  const [getWorkspaceFromApi, { data: workspaceData, loading: workspaceLoading, called: workspaceCalled }] =
    useWorkspaceLazyQueries({
      queryName: 'getWorkspaceCompanyPlan',
      errorLog: props.errorLog,
    })

  const workspaceLanguages = useMemo(
    (): TWorkspaceLanguages =>
      (workspaceData?.getWorkspace?.company?.companyPlan?.languages as TWorkspaceLanguages) || [],
    [workspaceData]
  )

  const workspaceTaxonomyType = workspaceData?.getWorkspace?.taxonomyType

  const getWorkspaceCompanyPlanFromApi = useCallback(() => getWorkspaceFromApi(), [getWorkspaceFromApi])

  const companyPlan = workspaceData?.getWorkspace?.company?.companyPlan
  const fallbackPlan = workspaceData?.getWorkspace?.company?.plan

  const workspaceCompanyPlan = useMemo(
    () =>
      companyPlan
        ? {
            ...companyPlan,
            jobPins: companyPlan.jobPins ?? fallbackPlan?.jobPins,
            jobPinnedCreators: companyPlan.jobPinnedCreators ?? fallbackPlan?.jobPinnedCreators,
            jobPinnedPosts: companyPlan.jobPinnedPosts ?? fallbackPlan?.jobPinnedPosts,
            jobPinnedSubtopics: companyPlan.jobPinnedSubtopics ?? fallbackPlan?.jobPinnedSubtopics,
            jobPinnedTopics: companyPlan.jobPinnedTopics ?? fallbackPlan?.jobPinnedTopics,
            jobPinnedSavedSearches: companyPlan.jobPinnedSavedSearches ?? fallbackPlan?.jobPinnedSavedSearches,
            jobFolders: companyPlan.jobFolders ?? fallbackPlan?.jobFolders,
            jobs: companyPlan.jobs ?? fallbackPlan?.jobs,
            workspaces: companyPlan.workspaces ?? fallbackPlan?.workspaces,
            editors: companyPlan.editors ?? fallbackPlan?.editors,
            viewers: companyPlan.viewers ?? fallbackPlan?.viewers,
          }
        : companyPlan,
    [companyPlan, fallbackPlan]
  )

  return {
    workspaceLanguages,
    workspaceCompanyPlan,
    canGenerateBriefing: workspaceData?.getWorkspace?.canGenerateBriefing,
    canShowGenerateBriefingButton: workspaceData?.getWorkspace?.canShowGenerateBriefingButton,
    workspaceCompanyPlanLoading: workspaceLoading,
    workspaceCompanyPlanCalled: workspaceCalled,
    getWorkspaceCompanyPlanFromApi,
    workspaceTaxonomyType,
  }
}
